import {
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import {
  CHANNEL_STATE,
  CHANNEL_STATE_LABELS,
  SETUP_TYPE,
  SUB_MODE,
  TS_PATHS,
  validateChannelParams,
} from 'features/plc/contracts/testStand.schema';
import {
  usePLCValue,
  usePLCValues,
  usePLCWriteState,
} from 'features/plc/providers/PLCProvider';
import {
  buildPLCSnapshot,
  getSnapshotHandles,
} from 'features/testStand/utils/plcSnapshot';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';

import { useTestStand } from '../../providers/TestStandProvider';

const STATE_BADGE_COLOR = {
  [CHANNEL_STATE.IDLE]: 'grey',
  [CHANNEL_STATE.COMPLETE]: 'green',
  [CHANNEL_STATE.ERROR]: 'red',
};

const pathShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
});

function statusBadgeColor(state) {
  return STATE_BADGE_COLOR[state] ?? 'blue';
}

function statusLabel(state) {
  if (state === undefined || state === null) return 'Unknown';
  if (state === CHANNEL_STATE.IDLE) return 'Idle';
  if (state === CHANNEL_STATE.COMPLETE) return 'Complete';
  if (state === CHANNEL_STATE.ERROR) return 'Error';
  return 'Running';
}

function formatDurationMs(ms) {
  if (!ms || ms <= 0) return '';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function pumpStatusLabel({
  busy, done, error, errorCode,
}) {
  if (error) return `Error${errorCode ? ` (${errorCode})` : ''}`;
  if (busy) return 'Running';
  if (done) return 'Done';
  return 'Idle';
}

function pumpStatusColor({ busy, done, error }) {
  if (error) return 'red';
  if (busy) return 'blue';
  if (done) return 'green';
  return 'grey';
}

function validateStartBothParams(lipidParams, aqueousParams, setupType, subMode) {
  const lipidError = validateChannelParams(lipidParams, setupType, subMode);
  if (lipidError) return lipidError;

  const aqueousError = validateChannelParams(aqueousParams, setupType, subMode);
  if (aqueousError) return aqueousError;

  if (setupType === SETUP_TYPE.DISCRETE_FLOW && subMode === SUB_MODE.DISPLACEMENT) {
    const lipidHold = Number(lipidParams.holdTimeAfterDispense ?? 0);
    const aqueousHold = Number(aqueousParams.holdTimeAfterDispense ?? 0);
    if (lipidHold !== aqueousHold) {
      return 'Hold after dispense must match for both channels.';
    }
  }

  return '';
}

function useChannelRunning(paths) {
  const channelState = usePLCValue(paths.status.state);
  const runActive = usePLCValue(paths.status.runActive);

  return runActive || (
    channelState !== undefined
    && channelState !== CHANNEL_STATE.IDLE
    && channelState !== CHANNEL_STATE.COMPLETE
    && channelState !== CHANNEL_STATE.ERROR
  );
}

function ChannelRunControl({
  channelLabel,
  channelParams,
  paths,
  plcSnapshot,
  saveTestLog,
  isSaving,
  setupType,
  subMode,
  existingTestLogId = undefined,
}) {
  const { runWrite, activeWrite } = usePLCWriteState();

  const channelState = usePLCValue(paths.status.state);
  const currentStep = usePLCValue(paths.status.currentStep);
  const stepRemainingMs = usePLCValue(paths.status.stepRemainingMs);
  const awaitingUser = usePLCValue(paths.status.awaitingUser);
  const runActive = usePLCValue(paths.status.runActive);
  const pressureEnable = usePLCValue(paths.actuators.pressureEnable);
  const pressureSetpoint = usePLCValue(paths.actuators.pressureSetpoint);
  const pneumaticValvePosition = usePLCValue(paths.actuators.pneumaticValvePosition);
  const pumpCommand = usePLCValue(paths.actuators.pump.txCommand);
  const pumpCommandLabel = usePLCValue(paths.actuators.pump.commandLabel);
  const pumpSequence = usePLCValue(paths.actuators.pump.sequence);
  const pumpLastResponse = usePLCValue(paths.actuators.pump.lastResponse);
  const pumpValvePosition = usePLCValue(paths.actuators.pump.valvePosition);
  const pumpBusy = usePLCValue(paths.actuators.pump.busy);
  const pumpDone = usePLCValue(paths.actuators.pump.done);
  const pumpError = usePLCValue(paths.actuators.pump.error);
  const pumpErrorCode = usePLCValue(paths.actuators.pump.errorCode);

  const isRunning = runActive || (
    channelState !== undefined
    && channelState !== CHANNEL_STATE.IDLE
    && channelState !== CHANNEL_STATE.COMPLETE
    && channelState !== CHANNEL_STATE.ERROR
  );
  const savingTestLog = isSaving;
  const showTimer = stepRemainingMs > 0;
  const headerText = showTimer
    ? `${channelLabel} - ${formatDurationMs(stepRemainingMs)}`
    : channelLabel;
  const showPumpStatus = setupType === SETUP_TYPE.DISCRETE_FLOW;
  const pumpStatus = {
    busy: pumpBusy,
    done: pumpDone,
    error: pumpError,
    errorCode: pumpErrorCode,
  };
  const recipeError = validateChannelParams(channelParams, setupType, subMode);

  const handleStart = useCallback(async () => {
    if (recipeError) return;

    const testLogId = existingTestLogId || await saveTestLog('started', plcSnapshot);
    await runWrite({ path: TS_PATHS.run.testLogId, value: testLogId });
    await runWrite({ path: paths.cmd.startRun, value: true });
  }, [runWrite, paths, plcSnapshot, saveTestLog, existingTestLogId, recipeError]);

  const handleStop = useCallback(() => runWrite({
    path: paths.cmd.stopRun,
    value: true,
  }), [runWrite, paths]);
  const handleAbort = useCallback(() => runWrite({
    path: paths.cmd.abortRun,
    value: true,
  }), [runWrite, paths]);
  const handleContinue = useCallback(() => runWrite({
    path: paths.cmd.continueRun,
    value: true,
  }), [runWrite, paths]);

  const writing = Boolean(activeWrite);

  return (
    <Container header={<Header variant="h3">{headerText}</Header>}>
      <SpaceBetween size="s">
        <ColumnLayout columns={2}>
          <Box>
            <Box variant="awsui-key-label">Status</Box>
            <Badge color={statusBadgeColor(channelState)}>
              {statusLabel(channelState)}
            </Badge>
          </Box>
          <Box>
            <Box variant="awsui-key-label">Current step</Box>
            <Box variant="p" color="text-status-info">
              {currentStep || CHANNEL_STATE_LABELS[channelState] || '—'}
            </Box>
          </Box>
        </ColumnLayout>
        {recipeError ? (
          <Box color="text-status-error">
            {recipeError}
          </Box>
        ) : null}
        <ColumnLayout columns={showPumpStatus ? 3 : 1}>
          <Box>
            <Box variant="awsui-key-label">Pressure command</Box>
            <Box>{pressureEnable ? `${Number(pressureSetpoint || 0).toFixed(3)} bar` : 'Off'}</Box>
          </Box>
          {showPumpStatus ? (
            <>
              <Box>
                <Box variant="awsui-key-label">Pneumatic valve</Box>
                <Box>{pneumaticValvePosition || '—'}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Pump status</Box>
                <Badge color={pumpStatusColor(pumpStatus)}>
                  {pumpStatusLabel(pumpStatus)}
                </Badge>
              </Box>
            </>
          ) : null}
        </ColumnLayout>
        {showPumpStatus ? (
          <ColumnLayout columns={3}>
            <Box>
              <Box variant="awsui-key-label">Pump command</Box>
              <Box>
                {pumpCommand ? `${pumpCommand} (#${pumpSequence || 0})` : '—'}
                {pumpCommandLabel ? ` — ${pumpCommandLabel}` : ''}
              </Box>
            </Box>
            <Box>
              <Box variant="awsui-key-label">Pump valve</Box>
              <Box>{pumpValvePosition || '—'}</Box>
            </Box>
            <Box>
              <Box variant="awsui-key-label">Pump response</Box>
              <Box>{pumpLastResponse || '—'}</Box>
            </Box>
          </ColumnLayout>
        ) : null}
        <SpaceBetween size="xs" direction="horizontal">
          <Button
            variant="primary"
            loading={savingTestLog || writing}
            disabled={isRunning || savingTestLog || writing || Boolean(recipeError)}
            onClick={handleStart}
          >
            Start
          </Button>
          <Button
            disabled={!isRunning || writing}
            onClick={handleStop}
          >
            Stop
          </Button>
          <Button
            disabled={!awaitingUser || writing}
            onClick={handleContinue}
          >
            Continue
          </Button>
          <Button
            variant="link"
            disabled={!isRunning || writing}
            onClick={handleAbort}
          >
            Abort
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
}
ChannelRunControl.propTypes = {
  existingTestLogId: PropTypes.string,
  channelParams: PropTypes.shape({}).isRequired,
  channelLabel: PropTypes.string.isRequired,
  paths: PropTypes.shape({
    status: PropTypes.shape({
      state: pathShape.isRequired,
      currentStep: pathShape.isRequired,
      stepRemainingMs: pathShape.isRequired,
      awaitingUser: pathShape.isRequired,
      runActive: pathShape.isRequired,
    }).isRequired,
    actuators: PropTypes.shape({
      pressureEnable: pathShape.isRequired,
      pressureSetpoint: pathShape.isRequired,
      pneumaticValvePosition: pathShape.isRequired,
      pump: PropTypes.shape({
        txCommand: pathShape.isRequired,
        commandLabel: pathShape.isRequired,
        sequence: pathShape.isRequired,
        lastResponse: pathShape.isRequired,
        valvePosition: pathShape.isRequired,
        busy: pathShape.isRequired,
        done: pathShape.isRequired,
        error: pathShape.isRequired,
        errorCode: pathShape.isRequired,
      }).isRequired,
    }).isRequired,
    cmd: PropTypes.shape({
      params: PropTypes.shape({
        targetPressure: pathShape,
        dispenseTime: pathShape,
        sampleVolume: pathShape,
        airSlugVolume: pathShape,
        aspirateSpeed: pathShape,
        dispenseVolume: pathShape,
        dispenseSpeed: pathShape,
        retractVolume: pathShape,
      }).isRequired,
      startRun: pathShape.isRequired,
      stopRun: pathShape.isRequired,
      abortRun: pathShape.isRequired,
      continueRun: pathShape.isRequired,
    }).isRequired,
  }).isRequired,
  plcSnapshot: PropTypes.shape({}).isRequired,
  saveTestLog: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  setupType: PropTypes.number.isRequired,
  subMode: PropTypes.number.isRequired,
};

const TERMINAL_STATES = new Set([CHANNEL_STATE.COMPLETE, CHANNEL_STATE.ERROR]);

function useStopWatcher(channelState, activeTestLogId, markRunStopped) {
  const prevState = useRef(undefined);

  useEffect(() => {
    const prev = prevState.current;
    prevState.current = channelState;

    if (!activeTestLogId) return;
    if (prev === undefined) return; // skip initial render
    if (!TERMINAL_STATES.has(channelState)) return;
    if (TERMINAL_STATES.has(prev)) return; // already terminal, no transition

    const status = channelState === CHANNEL_STATE.COMPLETE ? 'complete' : 'error';
    markRunStopped(status);
  }, [channelState, activeTestLogId, markRunStopped]);
}

function RunControls() {
  const { state: pageState, saveTestLog, markRunStopped } = useTestStand();
  const { runWrite, activeWrite } = usePLCWriteState();
  const setupType = usePLCValue(TS_PATHS.run.setupType) || SETUP_TYPE.CONTINUOUS_FLOW;
  const subMode = usePLCValue(TS_PATHS.run.subMode) || SUB_MODE.DISPLACEMENT;
  const plcValues = usePLCValues(getSnapshotHandles(setupType, subMode));
  const plcSnapshot = buildPLCSnapshot(plcValues);
  const lipidRunning = useChannelRunning(TS_PATHS.lipid);
  const aqueousRunning = useChannelRunning(TS_PATHS.aqueous);
  const lipidChannelState = usePLCValue(TS_PATHS.lipid.status.state);
  const aqueousChannelState = usePLCValue(TS_PATHS.aqueous.status.state);
  const lipidAwaitingUser = usePLCValue(TS_PATHS.lipid.status.awaitingUser);
  const aqueousAwaitingUser = usePLCValue(TS_PATHS.aqueous.status.awaitingUser);
  const writing = Boolean(activeWrite);

  const activeTestLogId = pageState.activeTestLog?.runId || pageState.testLogId;
  useStopWatcher(lipidChannelState, activeTestLogId, markRunStopped);
  useStopWatcher(aqueousChannelState, activeTestLogId, markRunStopped);
  const savingTestLog = pageState.saveState.status === 'saving';
  const eitherRunning = lipidRunning || aqueousRunning;
  const bothRunning = lipidRunning && aqueousRunning;
  const lipidParams = plcSnapshot.channels.lipid.params;
  const aqueousParams = plcSnapshot.channels.aqueous.params;
  const startBothError = validateStartBothParams(
    lipidParams,
    aqueousParams,
    setupType,
    subMode,
  );

  const handleStartBoth = useCallback(async () => {
    if (startBothError) return;

    const testLogId = await saveTestLog('started', plcSnapshot, { forceNew: true });
    await runWrite({ path: TS_PATHS.run.testLogId, value: testLogId });
    await runWrite({ path: TS_PATHS.run.startBoth, value: true });
  }, [runWrite, plcSnapshot, saveTestLog, startBothError]);

  const handleStopBoth = useCallback(async () => {
    await runWrite({ path: TS_PATHS.run.stopBoth, value: true });
  }, [runWrite]);

  const handleAbortBoth = useCallback(async () => {
    await runWrite({ path: TS_PATHS.run.abortBoth, value: true });
  }, [runWrite]);

  const handleContinueBoth = useCallback(async () => {
    await runWrite({ path: TS_PATHS.run.continueBoth, value: true });
  }, [runWrite]);

  return (
    <Container header={<Header variant="h2">Run Controls</Header>}>
      <SpaceBetween size="m">
        <SpaceBetween size="xs" direction="horizontal">
          <Button
            variant="primary"
            loading={savingTestLog || writing}
            disabled={eitherRunning || savingTestLog || writing || Boolean(startBothError)}
            onClick={handleStartBoth}
          >
            Start both
          </Button>
          <Button
            disabled={!eitherRunning || writing}
            onClick={handleStopBoth}
          >
            Stop both
          </Button>
          <Button
            disabled={(!lipidAwaitingUser && !aqueousAwaitingUser) || writing}
            onClick={handleContinueBoth}
          >
            Continue both
          </Button>
          <Button
            variant="link"
            disabled={!eitherRunning || writing}
            onClick={handleAbortBoth}
          >
            Abort both
          </Button>
          <Badge color={bothRunning ? 'green' : 'grey'}>
            {bothRunning ? 'Both running' : 'Independent control available'}
          </Badge>
        </SpaceBetween>
        {startBothError ? (
          <Box color="text-status-error">
            {startBothError}
          </Box>
        ) : null}
        <ColumnLayout columns={2}>
          <ChannelRunControl
            channelLabel="Lipid channel"
            channelParams={plcSnapshot.channels.lipid.params}
            paths={TS_PATHS.lipid}
            plcSnapshot={plcSnapshot}
            saveTestLog={saveTestLog}
            isSaving={savingTestLog}
            setupType={setupType}
            subMode={subMode}
            existingTestLogId={pageState.testLogId || undefined}
          />
          <ChannelRunControl
            channelLabel="Aqueous channel"
            channelParams={plcSnapshot.channels.aqueous.params}
            paths={TS_PATHS.aqueous}
            plcSnapshot={plcSnapshot}
            saveTestLog={saveTestLog}
            isSaving={savingTestLog}
            setupType={setupType}
            subMode={subMode}
            existingTestLogId={pageState.testLogId || undefined}
          />
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  );
}

export default RunControls;
